/**
 * Persian text hygiene.
 *
 * Persian and Arabic share a script but not all codepoints, and keyboards
 * disagree. The Arabic yeh (ي U+064A) and kaf (ك U+0643) look almost identical
 * to the Persian yeh (ی U+06CC) and kaf (ک U+06A9) but are different characters,
 * so "کتاب" typed on an Arabic keyboard will never equal "کتاب" typed on a
 * Persian one. Left unhandled, search and duplicate detection silently miss
 * matches and users see the same card twice with no idea why.
 */

const ARABIC_TO_PERSIAN: Record<string, string> = {
  "ي": "ی", // ي → ی  arabic yeh
  "ى": "ی", // ى → ی  alef maksura
  "ك": "ک", // ك → ک  arabic kaf
  "ڪ": "ک", // ڪ → ک  swash kaf
};

/** Harakat / tashkeel — optional vowel marks that shouldn't affect identity. */
const DIACRITICS = /[ً-ْٰٓ-ٕـ]/g;

/** Arabic-Indic ٠-٩ and Extended (Persian) ۰-۹ digits. */
const ARABIC_INDIC = /[٠-٩]/g;
const PERSIAN_DIGITS = /[۰-۹]/g;

/**
 * Normalize for STORAGE. Fixes character confusables and strips marks, while
 * preserving ZWNJ (U+200C) — that one is semantic, not decoration: "می‌رود"
 * without it becomes "میرود", which is a different (wrong) spelling.
 */
export function normalizePersian(input: string): string {
  let s = input.normalize("NFC");
  for (const [from, to] of Object.entries(ARABIC_TO_PERSIAN)) {
    s = s.replaceAll(from, to);
  }
  s = s.replace(DIACRITICS, "");
  s = s.replace(/\s+/g, " ").trim();
  // Collapse runs of ZWNJ and drop any that ended up adjacent to a space.
  s = s.replace(/‌{2,}/g, "‌").replace(/\s‌|‌\s/g, " ");
  return s;
}

/** Convert Arabic-Indic and Persian digits to ASCII. */
export function toAsciiDigits(input: string): string {
  return input
    .replace(ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(PERSIAN_DIGITS, (d) => String(d.charCodeAt(0) - 0x06f0));
}

/**
 * Aggressive key for COMPARISON only — never store or display this.
 * Additionally folds ZWNJ and alef/heh variants so "می‌رود" == "میرود" and
 * "آب" == "اب" when deciding whether two cards are duplicates.
 */
export function persianKey(input: string): string {
  return normalizePersian(input)
    .replace(/‌/g, "")
    .replace(/[آأإاٱ]/g, "ا") // آ أ إ ا ٱ → ا
    .replace(/[ةۀ]/g, "ه") // ة ۀ → ه
    .replace(/ؤ/g, "و") // ؤ → و
    .replace(/[ءٕٔ]/g, "") // stray hamza
    .replace(/\s/g, "")
    .toLowerCase();
}

/** Comparison key for the English side. */
export function englishKey(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip accents (café → cafe)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** True when a string contains any Arabic-script character. */
export function hasPersian(input: string): boolean {
  return /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(input);
}

/**
 * Wrap for safe display inside mixed-direction text.
 *
 * An EN↔FA app is inherently bidirectional, so direction belongs on the text
 * node — never on <body>. Setting it page-wide is what makes English phrases
 * render with their punctuation flipped to the wrong end.
 */
export function dirAttrs(text: string): { dir: "rtl" | "ltr"; lang: "fa" | "en" } {
  return hasPersian(text) ? { dir: "rtl", lang: "fa" } : { dir: "ltr", lang: "en" };
}
